import React, { useState, useEffect } from 'react';
import { Container, Table, Form, Row, Col } from 'react-bootstrap';
import { getSpecialProducts, getSpecialProductStatus, updateSpecialProduct, getFurnitureTypes } from '../api';
import Pagination from '../components/Global/Pagination';
import SortableHeader from '../components/ui/SortableHeader';
import { useSorting } from '../hooks/useSorting';
import { getSpecialProductStatusColor } from '../utils/specialProductStatusColors';
import useFilterPersistence from '../hooks/useFilterPersistence';

const SpecialProductsManagementPage = () => {
    const [statusOptions, setStatusOptions] = useState([]);
    const [furnitureTypes, setFurnitureTypes] = useState([]);
    const [specialProducts, setSpecialProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const [selectedStateFilter, setSelectedStateFilter] = useFilterPersistence('filters_special_products', '');
    const [selectedTypeFilter, setSelectedTypeFilter] = useFilterPersistence('filters_special_products_type', '');
    const [pagination, setPagination] = useFilterPersistence('pagination_special_products', { page: 1, pageSize: 50 });
    const currentPage = pagination.page;
    const itemsPerPage = pagination.pageSize;
    const { orderBy, orderDesc, handleSort, getSortIcon } = useSorting();

    useEffect(() => {
        getSpecialProductStatus()
            .then(data => setStatusOptions(data))
            .catch(error => console.error('Error cargando estados:', error));
        getFurnitureTypes(false)
            .then(data => setFurnitureTypes(data))
            .catch(error => console.error('Error cargando tipos:', error));
    }, []);

    useEffect(() => {
        fetchSpecialProducts();
    }, [currentPage, itemsPerPage, selectedStateFilter, selectedTypeFilter, orderBy, orderDesc]);

    const fetchSpecialProducts = async () => {
        setLoading(true);
        try {
            const filters = {};
            if (selectedStateFilter) filters.stateValueId = selectedStateFilter;
            if (selectedTypeFilter) filters.typeId = selectedTypeFilter;

            const response = await getSpecialProducts(currentPage, itemsPerPage, filters, orderBy, orderDesc);
            setSpecialProducts(response.items || []);
            setTotalCount(response.totalCount || 0);
        } catch (error) {
            console.error('Error cargando productos especiales:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page) => {
        setPagination({ ...pagination, page });
    };

    const handleItemsPerPageChange = (perPage) => {
        setPagination({ page: 1, pageSize: perPage });
    };



    const handleTypeChange = async (productId, productTypeId) => {
        try {
            await updateSpecialProduct(productId, { productTypeId: productTypeId ? Number(productTypeId) : null });
            setSpecialProducts(prev =>
                prev.map(p => p.id === productId ? { ...p, productTypeId: productTypeId ? Number(productTypeId) : null } : p)
            );
        } catch (error) {
            console.error('Error actualizando tipo de producto:', error);
        }
    };

    const handleStateChange = async (productId, stateValueId) => {
        try {
            await updateSpecialProduct(productId, { stateValueId: Number(stateValueId) });
            fetchSpecialProducts();
        } catch (error) {
            console.error('Error actualizando estado del producto especial:', error);
        }
    };

    const handleStateFilterChange = (stateId) => {
        setSelectedStateFilter(stateId);
        setPagination({ ...pagination, page: 1 });
    };

    const handleTypeFilterChange = (typeId) => {
        setSelectedTypeFilter(typeId);
        setPagination({ ...pagination, page: 1 });
    };

    const columns = [
        { key: 'id', label: 'ID' },
        { 
            key: 'order', 
            label: 'Pedido', 
            render: (item) => (
                <a href={`/orders/${item.orderId}`} target="_blank" rel="noopener noreferrer">
                    {item.order?.externalId || `#${item.orderId}`}
                </a>
            )
        },
        {
            key: 'orderStatus',
            label: 'Estado Pedido',
            render: (item) => item.order?.status || 'N/A'
        },
        { key: 'description', label: 'Descripción' },
        { 
            key: 'price', 
            label: 'Precio', 
            render: (item) => `${item.price}€`
        },
        { 
            key: 'quantity', 
            label: 'Cantidad', 
            render: (item) => item.quantity || 1
        },
        {
            key: 'productType',
            label: 'Tipo de Producto',
            render: (item) => (
                <Form.Select
                    size="sm"
                    value={item.productTypeId || ''}
                    onChange={(e) => handleTypeChange(item.id, e.target.value)}
                    style={{ width: 'auto', minWidth: '130px' }}
                >
                    <option value="">Sin tipo</option>
                    {furnitureTypes.map(type => (
                        <option key={type.id} value={type.id}>{type.typeName}</option>
                    ))}
                </Form.Select>
            )
        },
        {
            key: 'updatedAt',
            label: 'Última Modificación',
            render: (item) => {
                const date = item.updatedAt ? new Date(item.updatedAt) : null;
                return date ? date.toLocaleString() : 'N/A';
            }
        }
    ];

    return (
        <Container fluid className="my-4">
            <h1 className="mb-4">Gestión de Productos Especiales</h1>

            <Row className="mb-3">
                <Col xs={12} className="d-flex justify-content-start gap-3">
                    <Form.Group>
                        <Form.Label className="me-2">Filtrar por estado:</Form.Label>
                        <Form.Select 
                            data-testid="status-filter-select"
                            value={selectedStateFilter} 
                            onChange={(e) => handleStateFilterChange(e.target.value)}
                            style={{width: 'auto', display: 'inline-block'}}
                        >
                            <option value="">Todos los estados</option>
                            {statusOptions.map(state => {
                                const stateColor = getSpecialProductStatusColor(state.valueId);
                                return (
                                    <option 
                                        key={state.valueId} 
                                        value={state.valueId}
                                        style={{
                                            backgroundColor: stateColor.bg,
                                            color: stateColor.text
                                        }}
                                    >
                                        {state.name}
                                    </option>
                                );
                            })}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label className="me-2">Filtrar por tipo:</Form.Label>
                        <Form.Select
                            data-testid="type-filter-select"
                            value={selectedTypeFilter}
                            onChange={(e) => handleTypeFilterChange(e.target.value)}
                            style={{width: 'auto', display: 'inline-block'}}
                        >
                            <option value="">Todos los tipos</option>
                            {furnitureTypes.map(type => (
                                <option key={type.id} value={type.id}>{type.typeName}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>

            {loading ? (
                <p data-testid="loading-indicator">Cargando...</p>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <Table striped bordered hover responsive="sm" data-testid="special-products-table">
                        <thead>
                            <tr>
                                <SortableHeader column="id" onSort={handleSort} getSortIcon={getSortIcon}>ID</SortableHeader>
                                <SortableHeader column="orderId" onSort={handleSort} getSortIcon={getSortIcon}>Pedido</SortableHeader>
                                <SortableHeader column="order.status" onSort={handleSort} getSortIcon={getSortIcon}>Estado Pedido</SortableHeader>
                                <SortableHeader column="description" onSort={handleSort} getSortIcon={getSortIcon}>Descripción</SortableHeader>
                                <SortableHeader column="price" onSort={handleSort} getSortIcon={getSortIcon}>Precio</SortableHeader>
                                <SortableHeader column="quantity" onSort={handleSort} getSortIcon={getSortIcon}>Cantidad</SortableHeader>
                                <SortableHeader column="productTypeId" onSort={handleSort} getSortIcon={getSortIcon}>Tipo de Producto</SortableHeader>
                                <SortableHeader column="updatedAt" onSort={handleSort} getSortIcon={getSortIcon}>Última Modificación</SortableHeader>
                                <th>Estado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {specialProducts.length > 0 ? (
                                specialProducts.map((product) => (
                                    <tr key={product.id} data-testid={`product-row-${product.id}`}>
                                        {columns.map((column, colIndex) => (
                                            <td key={colIndex}>
                                                {column.render ? column.render(product) : product[column.key]}
                                            </td>
                                        ))}
                                        <td>
                                            <Form.Select
                                                data-testid={`status-select-${product.id}`}
                                                size="sm"
                                                value={product.stateValueId || ''}
                                                onChange={(e) => handleStateChange(product.id, e.target.value)}
                                                style={{
                                                    width: 'auto',
                                                    ...(() => {
                                                        const selectedColor = getSpecialProductStatusColor(product.stateValueId);
                                                        return {
                                                            backgroundColor: selectedColor.bg,
                                                            color: selectedColor.text,
                                                            border: `1px solid ${selectedColor.bg}`
                                                        };
                                                    })()
                                                }}
                                            >
                                                {statusOptions.map(state => {
                                                    const stateColor = getSpecialProductStatusColor(state.valueId);
                                                    return (
                                                        <option 
                                                            key={state.valueId} 
                                                            value={state.valueId}
                                                            style={{
                                                                backgroundColor: stateColor.bg,
                                                                color: stateColor.text
                                                            }}
                                                        >
                                                            {state.name}
                                                        </option>
                                                    );
                                                })}
                                            </Form.Select>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={columns.length + 1} className="text-center" data-testid="no-products-message">
                                        No se encontraron productos especiales.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            )}

            <Pagination
                totalCount={totalCount}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
            />


        </Container>
    );
};

export default SpecialProductsManagementPage;